import React, { useEffect, useState, useRef } from "react";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getImageUrl, getProfileImageUrlByAccountTypeCode } from "helpers/Image.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { updateCommentLike } from "helpers/Comment.helper";
import { getCurrentLangName, getStudentNickname } from "helpers/String.helper";
import { IDiary, IDiaryComment, IDiaryLike } from "api/interfaces/profile.interface";
import { fetchDiaryDetail, toggleDiaryLike, fetchDiaryComments, createDiaryComment, toggleCommentLike, deleteDiaryComment, fetchDiaryLikes, IFetchDiaryCommentsResponse, ICreateDiaryCommentResponse } from "api/koach/v2/diary.api";
import VideoPlayer from "commons/video/VideoPlayer";
import { useSelector } from "react-redux";
import ReportPopup from "commons/popup/ReportPopup";
import { usePopup } from "contexts/PopupContext";
import LikePopupForDiary from "./LikePopupForDiary";

interface StudentDiaryDetailPopupProps {
    diaryId: string;
    onClose: () => void;
}

const StudentDiaryDetailPopup: React.FC<StudentDiaryDetailPopupProps> = ({ diaryId, onClose }) => {
    const [diary, setDiary] = useState<IDiary | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [commentText, setCommentText] = useState("");
    const [comments, setComments] = useState<IDiaryComment[]>([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const [commentsPagination, setCommentsPagination] = useState({ currentPage: 1, hasNextPage: false });
    const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false);
    const [activeCommentKebabMenu, setActiveCommentKebabMenu] = useState<number | null>(null);
    const commentKebabRef = useRef<HTMLDivElement>(null);
    const [showReportPopup, setShowReportPopup] = useState(false);
    const [commentToReport, setCommentToReport] = useState<{ id: number; content: string } | null>(null);
    const [diaryLikes, setDiaryLikes] = useState<IDiaryLike[]>([]); // 다이어리 좋아요 목록
    const [likingCommentIds, setLikingCommentIds] = useState<Set<number>>(new Set()); // 좋아요 처리 중인 코멘트 ID들

    // Redux store에서 사용자 정보 가져오기
    const userInfo = useSelector((state: any) => state.user.info);
    const { showPopup } = usePopup();

    useEffect(() => {
        fetchDiaryData();
    }, [diaryId]);

    // 팝업 내부 스크롤 이벤트 감지하여 코멘트 무한 스크롤
    useEffect(() => {
        let isScrolling = false; // 스크롤 중복 방지 플래그

        const handleScroll = (event: Event) => {
            const target = event.target as HTMLElement;
            if (!target) return;

            // 이미 스크롤 처리 중이면 무시
            if (isScrolling) return;

            const scrollTop = target.scrollTop;
            const scrollHeight = target.scrollHeight;
            const clientHeight = target.clientHeight;

            // 스크롤이 하단 근처에 도달했는지 확인 (100px 여유)
            if (scrollTop + clientHeight >= scrollHeight - 100) {
                if (!isLoadingMoreComments && commentsPagination.hasNextPage) {
                    console.log('Loading next page...');
                    isScrolling = true; // 스크롤 처리 시작

                    const nextPage = commentsPagination.currentPage + 1;
                    fetchCommentsData(nextPage, true).finally(() => {
                        // API 호출 완료 후 플래그 리셋 (1초 후)
                        setTimeout(() => {
                            isScrolling = false;
                        }, 1000);
                    });
                } else {
                    console.log('Cannot load next page:', {
                        isLoadingMore: isLoadingMoreComments,
                        hasNextPage: commentsPagination.hasNextPage
                    });
                }
            }
        };

        // 팝업의 스크롤 가능한 요소를 찾아서 이벤트 리스너 추가
        const popupElement = document.querySelector('.overflow-y-auto');
        if (popupElement) {
            console.log('Scroll event listener added to:', popupElement);
            popupElement.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                popupElement.removeEventListener('scroll', handleScroll);
            };
        } else {
            console.log('Popup element with .overflow-y-auto not found');
        }
    }, [commentsPagination.hasNextPage, isLoadingMoreComments, commentsPagination.currentPage]); // 필요한 의존성 추가

    const fetchDiaryData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const result = await fetchDiaryDetail(diaryId);
            setDiary(result.diary);

            // 다이어리 상세 정보를 성공적으로 가져온 후 코멘트 목록 호출
            await fetchCommentsData();

            // 좋아요 목록 가져오기 (최대 5명)
            try {
                const likesResult = await fetchDiaryLikes(diaryId, 1, 5);
                setDiaryLikes(likesResult.likeList);
            } catch (error) {
                console.error("좋아요 목록 조회 실패:", error);
                setDiaryLikes([]);
            }
        } catch (error) {
            console.error("다이어리 상세 조회 실패:", error);
            setError("다이어리를 불러오는데 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCommentsData = async (page: number = 1, append: boolean = false) => {
        console.log('fetchCommentsData called:', { page, append });

        try {
            if (append) {
                setIsLoadingMoreComments(true);
            } else {
                setCommentsLoading(true);
            }

            const result = await fetchDiaryComments(diaryId, page);
            console.log('API response:', result);

            if (append && comments.length > 0) {
                // 기존 코멘트에 새 코멘트 추가
                setComments(prevComments => [...prevComments, ...result.commentList]);
            } else {
                // 첫 페이지 로드
                setComments(result.commentList);
            }

            // 페이지네이션 정보 업데이트
            setCommentsPagination(prevPagination => {
                const newPagination = {
                    currentPage: result.pagination.currentPage,
                    hasNextPage: result.pagination.hasNextPage
                };
                console.log('Pagination updated:', newPagination);
                return newPagination;
            });

        } catch (error) {
            console.error("코멘트 목록 조회 실패:", error);
            if (!append) {
                setComments([]);
            }
        } finally {
            if (append) {
                setIsLoadingMoreComments(false);
            } else {
                setCommentsLoading(false);
            }
        }
    };

    const handleClose = () => {
        onClose();
    };

    const handleImageClick = (imageUrl: string) => {
        // 이미지 클릭 시 확대 보기 (필요시 구현)
        window.open(imageUrl, '_blank');
    };

    const handleVideoClick = (videoUrl: string) => {
        // 비디오 클릭 시 새 창에서 재생 (필요시 구현)
        window.open(videoUrl, '_blank');
    };

    const handleLikeToggle = async () => {
        try {
            const result = await toggleDiaryLike(diaryId);

            // 다이어리 데이터 업데이트
            setDiary(prevDiary => {
                if (!prevDiary) return prevDiary;
                return {
                    ...prevDiary,
                    isLiked: result.isLiked,
                    likeCount: result.likeCount || 0
                };
            });

            // 좋아요 목록 새로고침
            try {
                const likesResult = await fetchDiaryLikes(diaryId, 1, 5);
                setDiaryLikes(likesResult.likeList);
            } catch (error) {
                console.error("좋아요 목록 새로고침 실패:", error);
            }

        } catch (error) {
            console.error("좋아요 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        }
    };

    const handleLikeCountClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (diary && diary.likeCount && diary.likeCount > 0) {
            showPopup(
                <LikePopupForDiary
                    diaryId={diaryId}
                    onClose={() => showPopup(null)}
                />
            );
        }
    };

    const handleCommentSubmit = async () => {
        if (!commentText.trim() || isSubmittingComment) return;

        try {
            setIsSubmittingComment(true);

            // 코멘트 작성 API 호출
            const response = await createDiaryComment(diaryId, commentText.trim());
            console.log("코멘트 작성 응답:", response);

            // 입력란 초기화
            setCommentText("");

            // 새로 작성한 코멘트를 목록의 맨 위에 추가
            if (response && response.comment) {
                console.log("새 코멘트 추가:", response.comment);
                setComments(prevComments => {
                    const newComments = [response.comment, ...prevComments];
                    console.log("업데이트된 코멘트 목록:", newComments);
                    return newComments;
                });
                
                // 다이어리의 댓글 수도 업데이트
                setDiary(prevDiary => {
                    if (!prevDiary) return prevDiary;
                    return {
                        ...prevDiary,
                        commentCount: (prevDiary.commentCount || 0) + 1
                    };
                });
            } else {
                console.warn("코멘트 응답이 올바르지 않습니다:", response);
            }
        } catch (error) {
            console.error("코멘트 작성 실패:", error);
            // TODO: 에러 메시지 표시 (팝업 또는 토스트)
        } finally {
            setIsSubmittingComment(false);
        }
    };

    // 코멘트 케밥 메뉴 토글
    const toggleCommentKebabMenu = (commentId: number) => {
        setActiveCommentKebabMenu(activeCommentKebabMenu === commentId ? null : commentId);
    };

    // 내 코멘트인지 확인
    const isMyComment = (comment: IDiaryComment) => {
        return comment.user?.id === userInfo?.id;
    };

    // 코멘트 삭제 처리
    const handleDeleteComment = async (commentId: number) => {
        setActiveCommentKebabMenu(null);

        // 브라우저 confirm 팝업으로 삭제 확인
        const isConfirmed = window.confirm(
            getLanguageValue(PAGE_TYPE.KOACH, "diary.txtDeleteCommentMessage") ||
            "정말로 이 코멘트를 삭제하시겠습니까?"
        );

        if (!isConfirmed) return;

        try {
            await deleteDiaryComment(diaryId, commentId);
            console.log('코멘트 삭제 성공:', commentId);

            // 코멘트 목록에서 삭제된 코멘트 제거
            setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));

            // 삭제된 코멘트가 마지막 페이지의 마지막 코멘트였다면 이전 페이지로 이동
            if (comments.length === 1 && commentsPagination.currentPage > 1) {
                const prevPage = commentsPagination.currentPage - 1;
                await fetchCommentsData(prevPage, false);
            }
        } catch (error) {
            console.error('코멘트 삭제 실패:', error);
            // TODO: 에러 메시지 표시
        }
    };

    // 코멘트 신고 팝업 표시
    const handleReportComment = (commentId: number, commentContent: string) => {
        setCommentToReport({ id: commentId, content: commentContent });
        setShowReportPopup(true);
        setActiveCommentKebabMenu(null);
    };



    // 케밥 메뉴 외부 클릭 감지
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (commentKebabRef.current && !commentKebabRef.current.contains(event.target as Node)) {
                setActiveCommentKebabMenu(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleCommentLikeToggle = async (commentId: number, event?: React.MouseEvent) => {
        // 이벤트 전파 방지
        if (event) {
            event.stopPropagation();
        }

        // 이미 좋아요 처리 중인 코멘트인지 확인
        if (likingCommentIds.has(commentId)) {
            console.log("이미 좋아요 처리 중인 코멘트:", commentId);
            return;
        }

        try {
            // 좋아요 처리 중인 코멘트 ID 추가
            setLikingCommentIds(prev => new Set(prev).add(commentId));

            console.log("❤️ 댓글 좋아요 토글 시작:", commentId);
            const result = await toggleCommentLike(diaryId, commentId.toString());
            console.log("✅ 댓글 좋아요 토글 결과:", { commentId, result });

            // 헬퍼 함수를 사용하여 부모/자식 댓글 모두에서 해당 commentId 찾아서 업데이트
            setComments(prevComments =>
                updateCommentLike(prevComments, commentId, {
                    isLiked: result.isLiked,
                    likeCount: result.likeCount
                })
            );
        } catch (error) {
            console.error("❌ 댓글 좋아요 토글 실패:", error);
            // TODO: 에러 메시지 표시 (팝업 또는 토스트)
        } finally {
            // 좋아요 처리 완료 후 ID 제거
            setLikingCommentIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(commentId);
                return newSet;
            });
        }
    };



    // 로딩 상태
    if (isLoading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="animate-pulse p-6">
                        {/* 헤더 스켈레톤 */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                                <div>
                                    <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                                </div>
                            </div>
                            <div className="w-6 h-6 bg-gray-200 rounded"></div>
                        </div>

                        {/* 제목 스켈레톤 */}
                        <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>

                        {/* 내용 스켈레톤 */}
                        <div className="space-y-3 mb-6">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                        </div>

                        {/* 미디어 스켈레톤 */}
                        <div className="space-y-4">
                            {/* 비디오 스켈레톤 */}
                            <div>
                                <div className="h-4 bg-gray-200 rounded w-16 mb-3"></div>
                                <div className="h-48 bg-gray-200 rounded-lg"></div>
                            </div>

                            {/* 이미지 스켈레톤 */}
                            <div>
                                <div className="h-4 bg-gray-200 rounded w-16 mb-3"></div>
                                <div className="flex gap-3">
                                    <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                    <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                    <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                </div>
                            </div>
                        </div>

                        {/* 통계 정보 스켈레톤 */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-6">
                            <div className="flex items-center space-x-4">
                                <div className="h-4 bg-gray-200 rounded w-8"></div>
                                <div className="h-4 bg-gray-200 rounded w-8"></div>
                            </div>
                            <div className="h-3 bg-gray-200 rounded w-24"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 에러 상태
    if (error || !diary) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 p-6">
                    <div className="text-center">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {getLanguageValue(PAGE_TYPE.KOACH, "diary.txtLoadError") || "다이어리 로드 실패"}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {error || "다이어리를 불러올 수 없습니다."}
                        </p>
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            {getLanguageValue(PAGE_TYPE.KOACH, "diary.btnClose") || "닫기"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="popup-content bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* 헤더 - 스크롤해도 항상 상단에 고정 */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between py-2 px-4">
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 rounded-full overflow-hidden">
                                <img
                                    src={getImageUrl(diary.user.userStudent.profileImage) || "/imgs/dummy/basic-img.png"}
                                    alt={diary.user.userStudent.nickname}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">
                                    {diary.user.userStudent.nickname}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    {formatTimeAgo(new Date(diary.createdAt))}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* 다이어리 내용 */}
                <div className="p-6">
                    {/* 제목 */}
                    <h2 className="text-xl font-bold text-gray-900 mb-4">
                        {diary.title}
                    </h2>

                    {/* 내용 */}
                    <div className="text-gray-700 mb-6 whitespace-pre-wrap">
                        {diary.content}
                    </div>

                    {/* 비디오들 (우선 출력) */}
                    {diary.diaryVideos && diary.diaryVideos.length > 0 && (
                        <div className="mb-6">
                            <div className="space-y-3">
                                {diary.diaryVideos.map((diaryVideo) => (
                                    <VideoPlayer
                                        key={diaryVideo.id}
                                        video={diaryVideo.video}
                                        className="w-full h-full object-cover"
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 이미지들 (좌우 스크롤) */}
                    {diary.diaryImages && diary.diaryImages.length > 0 && (
                        <div className="mb-6">
                            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                {diary.diaryImages.map((diaryImage) => (
                                    <div
                                        key={diaryImage.id}
                                        className="flex-shrink-0 aspect-square w-32 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                                        onClick={() => handleImageClick(getImageUrl(diaryImage.image))}
                                    >
                                        <img
                                            src={getImageUrl(diaryImage.image)}
                                            alt={`다이어리 이미지 ${diaryImage.sequence}`}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 통계 정보 */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 mb-4">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                            {/* 좋아요 버튼 */}
                            <button
                                className="flex items-center space-x-1 hover:opacity-80 transition-opacity"
                                onClick={handleLikeToggle}
                            >
                                <svg
                                    className={`w-4 h-4 ${diary.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}
                                    fill={diary.isLiked ? '#CF3688' : 'none'}
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                <span 
                                    className={`${diary.isLiked ? 'text-[#CF3688]' : 'text-gray-500'} ${(diary.likeCount && diary.likeCount > 0) ? 'cursor-pointer' : ''}`}
                                    onClick={handleLikeCountClick}
                                >
                                    {diary.likeCount || 0}
                                </span>
                            </button>

                            {/* 댓글 수 */}
                            {diary.commentCount !== undefined && (
                                <div className="flex items-center space-x-1">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M21.99 4c0-1.1-.89-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18zM18 14H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
                                    </svg>
                                    <span>{diary.commentCount}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 좋아요 목록 영역 */}
                    {(() => {
                        const likeCount = diary.likeCount || 0;

                        if (likeCount === 0) {
                            return (
                                <div className="pt-2 pb-4 text-center">
                                    <p className="text-xs text-gray-400">제일 처음 좋아요를 눌러보세요</p>
                                </div>
                            );
                        }

                        if (diaryLikes.length === 0) {
                            return null; // 로딩 중이거나 데이터가 없을 때
                        }

                        const displayLikes = diaryLikes.slice(0, 5);

                        return (
                            <div 
                                className="pt-2 pb-4 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -mx-2 transition-colors"
                                onClick={handleLikeCountClick}
                            >
                                <div className="flex items-center space-x-2">
                                    <div className="flex -space-x-2">
                                        {displayLikes.map((like, index) => (
                                            <div
                                                key={like.userId}
                                                className="w-6 h-6 rounded-full border-2 border-white overflow-hidden shadow-sm bg-gray-100"
                                                style={{ zIndex: 5 - index }}
                                            >
                                                <img
                                                    src={getProfileImageUrlByAccountTypeCode(like?.user, 32, 32) || "/imgs/dummy/basic-img.png"}
                                                    alt={getStudentNickname(like?.user?.userStudent, like?.user?.username || '') || "User"}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = "/imgs/dummy/basic-img.png";
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <span className="text-xs text-gray-600 whitespace-nowrap flex-shrink-0">
                                        {diary.likeCount || 0}명이 좋아요를 눌렀어요❤️
                                    </span>
                                </div>
                            </div>
                        );
                    })()}

                    {/* 코멘트 섹션 */}
                    <div className="border-t border-gray-200 pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {getLanguageValue(PAGE_TYPE.KOACH, "diary.txtComments") || "코멘트"}
                        </h3>

                        {/* 코멘트 입력 영역 */}
                        <div className="flex gap-3 mb-6">
                            <input
                                type="text"
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder={getLanguageValue(PAGE_TYPE.KOACH, "diary.txtCommentPlaceholder") || "코멘트를 입력하세요..."}
                                maxLength={200}
                                disabled={isSubmittingComment}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CF3688] focus:border-[#CF3688] disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                                onKeyPress={(e) => e.key === 'Enter' && handleCommentSubmit()}
                            />
                            <button
                                onClick={handleCommentSubmit}
                                disabled={!commentText.trim() || isSubmittingComment}
                                className="px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#CF3688]/90 focus:outline-none focus:ring-2 focus:ring-[#CF3688] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                                {getLanguageValue(PAGE_TYPE.KOACH, "diary.btnCommentSubmit") || "입력"}
                            </button>
                        </div>

                        {/* 코멘트 목록 */}
                        <div className="space-y-1.5">
                            {commentsLoading ? (
                                // 코멘트 스켈레톤 UI
                                <>
                                    {[1, 2, 3].map((index) => (
                                        <div key={`skeleton-${index}`} className="border border-gray-200 rounded-lg p-3 animate-pulse">
                                            {/* 프로필 이미지 스켈레톤 */}
                                            <div className="flex items-center space-x-2 mb-1.5">
                                                <div className="w-7 h-7 bg-gray-300 rounded-full"></div>
                                                <div className="flex flex-col space-y-1.5">
                                                    <div className="h-4 bg-gray-300 rounded w-24"></div>
                                                    <div className="h-3 bg-gray-300 rounded w-16"></div>
                                                </div>
                                            </div>

                                            {/* 코멘트 내용 스켈레톤 */}
                                            <div className="space-y-2 mb-2">
                                                <div className="h-4 bg-gray-300 rounded w-full"></div>
                                                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                                            </div>

                                            {/* 좋아요 버튼 스켈레톤 */}
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 bg-gray-300 rounded"></div>
                                                    <div className="h-3 bg-gray-300 rounded w-8"></div>
                                                </div>
                                                <div className="h-3 bg-gray-300 rounded w-16"></div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            ) : comments.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    아직 작성된 코멘트가 없습니다.
                                </div>
                            ) : (
                                comments.map((comment) => (
                                    <div key={comment.id} className="border border-gray-200 rounded-lg p-3">
                                        {/* 코멘트 헤더 */}
                                        <div className="flex items-center space-x-2 mb-1.5">
                                            {/* 프로필 이미지/로고 */}
                                            <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
                                                {comment.user?.userStudent && (
                                                    <img
                                                        src={getImageUrl(comment.user?.userStudent?.profileImage) || "/imgs/dummy/basic-img.png"}
                                                        alt="프로필"
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                                {comment.user?.userKoach && (
                                                    <img
                                                        src={getImageUrl(comment.user?.userKoach?.profileImage) || "/imgs/dummy/basic-img.png"}
                                                        alt="프로필"
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                                {comment.user?.userSchool && (
                                                    <img
                                                        src={getImageUrl(comment.user?.userSchool?.logoImage) || "/imgs/dummy/school-logo/school-logo-1.png"}
                                                        alt="학원 로고"
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                                {comment.user?.userAgency && (
                                                    <img
                                                        src={getImageUrl(comment.user?.userAgency?.agency?.logoImage) || "/imgs/dummy/agency-logo/agency-logo-1.png"}
                                                        alt="기획사 로고"
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                            </div>

                                            {/* 사용자 정보 */}
                                            <div className="flex flex-col flex-1">
                                                <span className="font-medium text-gray-900 text-sm">
                                                    {comment.user?.userStudent && (
                                                        comment.user.userStudent.nickname ||
                                                        `${comment.user.userStudent.firstName} ${comment.user.userStudent.lastName}` ||
                                                        "알 수 없음"
                                                    )}
                                                    {comment.user?.userKoach && (
                                                        comment.user.userKoach.nickname ||
                                                        `${comment.user.userKoach.firstName} ${comment.user.userKoach.lastName}` ||
                                                        "알 수 없음"
                                                    )}
                                                    {comment.user?.userSchool && (
                                                        comment.user.userSchool.schoolName || "알 수 없음"
                                                    )}
                                                    {comment.user?.userAgency && (
                                                        comment.user.userAgency.agency?.nameKo || 
                                                        comment.user.userAgency.agency?.nameEn || 
                                                        "알 수 없음"
                                                    )}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {getCurrentLangName(comment.user?.accountType?.names) || comment.user?.accountTypeCode}
                                                </span>
                                            </div>

                                            {/* 케밥 버튼 */}
                                            <div className="relative flex-shrink-0">
                                                <button
                                                    onClick={() => toggleCommentKebabMenu(comment.id)}
                                                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                                >
                                                    <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                                    </svg>
                                                </button>

                                                {/* 케밥 메뉴 툴팁 */}
                                                {activeCommentKebabMenu === comment.id && (
                                                    <div
                                                        ref={commentKebabRef}
                                                        className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]"
                                                    >
                                                        {isMyComment(comment) ? (
                                                            <button
                                                                onClick={() => handleDeleteComment(comment.id)}
                                                                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                            >
                                                                삭제
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => handleReportComment(comment.id, comment.contents)}
                                                                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                            >
                                                                신고
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* 코멘트 내용 */}
                                        <p className="text-gray-700 mb-2">{comment.contents}</p>

                                        {/* 코멘트 좋아요 및 작성시간 */}
                                        <div className="flex items-center justify-between">
                                            {/* 좋아요 버튼 - 좌측 정렬 */}
                                            <button
                                                className={`flex items-center space-x-1 transition-opacity ${
                                                    likingCommentIds.has(comment.id)
                                                        ? 'cursor-not-allowed opacity-50'
                                                        : 'cursor-pointer hover:opacity-80'
                                                }`}
                                                onClick={(e) => {
                                                    e.stopPropagation(); // 항상 이벤트 전파 방지
                                                    if (!likingCommentIds.has(comment.id)) {
                                                        handleCommentLikeToggle(comment.id, e);
                                                    }
                                                }}
                                            >
                                                {likingCommentIds.has(comment.id) ? (
                                                    <svg
                                                        className="w-4 h-4 text-gray-400 animate-pulse"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        className={`w-4 h-4 ${comment.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}
                                                        fill={comment.isLiked ? '#CF3688' : 'none'}
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                    </svg>
                                                )}
                                                <span className={`text-xs ${comment.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}>
                                                    {comment.likeCount || 0}
                                                </span>
                                            </button>

                                            {/* 작성시간 - 우측 정렬 */}
                                            <span className="text-xs text-gray-400">
                                                {formatTimeAgo(comment.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}

                            {/* 추가 코멘트 로딩 상태 */}
                            {isLoadingMoreComments && (
                                <div className="text-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                    <span className="text-gray-500 text-sm">더 많은 코멘트를 불러오는 중...</span>
                                </div>
                            )}

                            {/* 더 이상 로드할 코멘트가 없을 때 */}
                            {!isLoadingMoreComments && !commentsPagination.hasNextPage && comments.length > 0 && (
                                <div className="text-center py-4">
                                    <span className="text-gray-400 text-sm">모든 코멘트를 불러왔습니다.</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 코멘트 신고 팝업 */}
            {showReportPopup && commentToReport && (
                <ReportPopup
                    type="comment"
                    diaryId={diaryId}
                    commentId={commentToReport.id.toString()}
                    content={commentToReport.content}
                    onClose={() => {
                        setShowReportPopup(false);
                        setCommentToReport(null);
                    }}
                    onSuccess={() => {
                        // 신고 성공 시 처리 (필요시 추가)
                        console.log('코멘트 신고가 완료되었습니다.');
                    }}
                />
            )}
        </div>
    );
};

export default StudentDiaryDetailPopup;
